import type { IGraphic, IGraphicAttribute } from '@visactor/vrender-core';
import type {
  GlyphChannelEncoder,
  GlyphDefaultEncoder,
  IComponentConstructor,
  IElement,
  IGlyphElement,
  IGlyphMeta,
  IGrammarBaseConstructor,
  IGroupMark,
  IInteractionConstructor,
  ILayoutOptions,
  IMark,
  IMarkConstructor,
  IPlotMarkConstructor,
  ISemanticMark,
  IStageEventPlugin,
  ITransform,
  IView,
  MarkType,
  TypeAnimation
} from '../types';
import { GlyphMeta } from '../view/glyph-meta';
import type { ComponentOptions } from '@visactor/vrender-components';

export class Factory {
  private static _plotMarks: Record<string, IPlotMarkConstructor> = {};
  private static _marks: Record<string, IMarkConstructor> = {};
  private static _components: Record<string, IComponentConstructor> = {};
  private static _graphicComponents: Record<string, (attrs: any, options?: ComponentOptions) => IGraphic> = {};
  private static _transforms: Record<string, ITransform> = {};
  private static _grammars: Record<string, { grammarClass: IGrammarBaseConstructor; specKey: string }> = {};
  private static _glyphs: Record<string, IGlyphMeta<any, any>> = {};
  private static _animations: Record<string, TypeAnimation<IGlyphElement> | TypeAnimation<IElement>> = {};
  private static _interactions: Record<string, IInteractionConstructor> = {};
  private static _graphics: Record<string, (attributes: IGraphicAttribute) => IGraphic> = {};
  private static defaultLayout: (marks: IMark[], options: ILayoutOptions, view: IView) => void;
  private static _stageEventPlugins: Record<string, IStageEventPlugin<any>> = {};

  static registerPlotMarks(key: string, mark: IPlotMarkConstructor) {
    Factory._plotMarks[key] = mark;
  }

  static createPlotMark(type: string, id?: string): ISemanticMark<any, any> | null {
    if (!Factory._plotMarks[type]) {
      return null;
    }
    const MarkConstructor = Factory._plotMarks[type];
    return new MarkConstructor(id);
  }

  static registerMark(key: string, mark: IMarkConstructor) {
    Factory._marks[key] = mark;
  }

  static createMark(type: string, view: IView, group?: IGroupMark): IMark | null {
    if (!Factory._marks[type]) {
      return null;
    }
    const MarkConstructor = Factory._marks[type];
    return new MarkConstructor(view, type, group);
  }

  static hasMark(type: string) {
    return !!Factory._marks[type];
  }

  static getMark(type: string) {
    return Factory._marks[type];
  }

  static registerComponent(key: string, component: IComponentConstructor) {
    Factory._components[key] = component;
  }

  static createComponent(componentType: string, view: IView, group?: IGroupMark, mode?: '2d' | '3d') {
    const Ctor = Factory._components[componentType];
    if (!Ctor) {
      return null;
    }

    return new Ctor(view, group, mode);
  }

  static hasComponent(componentType: string) {
    return !!Factory._components[componentType];
  }

  static registerGraphicComponent(key: string, creator: (attrs: any, options?: ComponentOptions) => IGraphic) {
    Factory._graphicComponents[key] = creator;
  }

  static createGraphicComponent(componentType: string, attrs: any, options?: ComponentOptions) {
    const compCreator = Factory._graphicComponents[componentType];

    if (!compCreator) {
      return null;
    }

    return compCreator(attrs, options);
  }

  static registerTransform(type: string, transform: Omit<ITransform, 'type'>, isBuiltIn?: boolean) {
    Factory._transforms[type] = Object.assign(transform, { type, isBuiltIn: !!isBuiltIn });
  }

  static getTransform(type: string) {
    return Factory._transforms[type];
  }

  static unregisterRuntimeTransforms() {
    Object.keys(Factory._transforms).forEach(type => {
      if (Factory._transforms[type] && !Factory._transforms[type].isBuiltIn) {
        Factory._transforms[type] = null;
      }
    });
  }

  static registerGrammar(type: string, grammarClass: IGrammarBaseConstructor, specKey?: string) {
    Factory._grammars[type] = {
      grammarClass: grammarClass,
      specKey: specKey ?? type
    };
  }

  static createGrammar(type: string, view: IView, grammarType: string) {
    const Ctor = Factory._grammars[type]?.grammarClass;

    if (!Ctor) {
      return null;
    }

    return new Ctor(view, grammarType);
  }

  static getGrammars() {
    return this._grammars;
  }

  static registerGlyph = <EncodeValuesType = any, GlyphConfigType = any>(
    glyphType: string,
    marks: { [markName: string]: MarkType },
    encoders?: { [markName: string]: GlyphChannelEncoder<any, EncodeValuesType, GlyphConfigType> },
    defaultEncoder?: GlyphDefaultEncoder,
    progressiveChannels?: string | string[]
  ): IGlyphMeta<EncodeValuesType, GlyphConfigType> => {
    Factory._glyphs[glyphType] = new GlyphMeta<EncodeValuesType, GlyphConfigType>(
      marks,
      encoders,
      defaultEncoder,
      progressiveChannels
    );
    return Factory._glyphs[glyphType];
  };

  static getGlyph(glyphType: string): IGlyphMeta {
    return Factory._glyphs[glyphType];
  }

  static registerAnimationType = (
    animationType: string,
    animation: TypeAnimation<IGlyphElement> | TypeAnimation<IElement>
  ): void => {
    Factory._animations[animationType] = animation;
  };

  static getAnimationType = (animationType: string) => {
    return Factory._animations[animationType];
  };

  static registerInteraction = (interactionType: string, interaction: IInteractionConstructor) => {
    Factory._interactions[interactionType] = interaction;
  };

  static createInteraction(interactionType: string, view: IView, options?: any) {
    const Ctor = Factory._interactions[interactionType];
    if (!Ctor) {
      return null;
    }

    return new Ctor(view, options);
  }

  static hasInteraction(interactionType: string) {
    return !!Factory._interactions[interactionType];
  }

  static registerGraphic = (graphicType: string, creator: (attributes: IGraphicAttribute) => IGraphic) => {
    Factory._graphics[graphicType] = creator;
  };

  static getGraphicType = (graphicType: string) => {
    return Factory._graphics[graphicType];
  };

  static createGraphic = (graphicType: string, attributes?: IGraphicAttribute) => {
    const creator = Factory._graphics[graphicType];
    if (!creator) {
      return null;
    }
    return creator(attributes);
  };

  static registerDefaultLayout = (layout: (marks: IMark[], options: ILayoutOptions, view: IView) => void) => {
    Factory.defaultLayout = layout;
  };

  static getDefaultLayout = () => {
    return Factory.defaultLayout;
  };

  static registerStageEventPlugin = (type: string, Plugin: IStageEventPlugin<any>) => {
    Factory._stageEventPlugins[type] = Plugin;
  };

  static getStageEventPlugin = (type: string) => {
    return Factory._stageEventPlugins[type];
  };
}
